import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { getMyTags } from '../services/tags';
import { reorderByOwner, tagsById as indexTags } from '../utils/tags';
import { getSessionKey, getSessionUserId, useOnSessionChange } from '../utils/session';

/**
 * MY PRIVATE TAGS (docs/plans/tags.md §5.3): the signed-in person's own tags,
 * one list for the whole app — Account › Tags manages it, and every surface
 * that shows or picks a private tag (the item picker, the chips, T4b) reads
 * it. The CardDefinitionsContext shape:
 *
 * - LAZY: nothing is asked until a consumer mounts (`useTags` /
 *   `useOptionalTags` call `ensureLoaded`), so a page with no tags on it
 *   never pays for them.
 * - NOTHING WITHOUT A SESSION: signed out, it settles EMPTY and asks nothing
 *   (`GET /api/tags?owner=me` is an authenticated read — a 401 on a public
 *   page is at best a wasted request).
 * - PER ACCOUNT: another account signing in on this tab (or none) drops the
 *   list at once, in a layout effect, before paint — a private tag is the
 *   one thing that must never render for the next person — and reloads it
 *   if anything had asked. An answer that lands for another session is
 *   dropped. Only a SUCCESSFUL load latches it: a failure is retried by the
 *   next consumer mount.
 * - `version` bumps on every successful load, for caches that hang off it.
 *
 * Exposes `{ tags, tagsById, loading, loaded, error, version, ensureLoaded,
 * refresh, replaceTags }`. `replaceTags(list)` is the optimistic write — a
 * manager's drag shows at once, and a refused save puts the old list back
 * through the same call. Tags come in tree order (`depth`, `path`).
 */
const TagsContext = createContext(null);

const EMPTY = Object.freeze([]);

export function TagsProvider({ children }) {
	const [tags, setTags] = useState(EMPTY);
	const [settled, setSettled] = useState(false);
	const [fetching, setFetching] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState(null);
	const [version, setVersion] = useState(0);
	// { session } once a load SUCCEEDED for that session; null until then.
	const loadedForRef = useRef(null);
	const inFlightRef = useRef(undefined); // the session of the load in flight
	// Has any consumer asked? A session change reloads only then.
	const wantedRef = useRef(false);

	const load = useCallback(async ({ force = false } = {}) => {
		wantedRef.current = true;
		if (!getSessionUserId()) {
			setSettled(true);
			return undefined;
		}
		const session = getSessionKey();
		if (!force) {
			if (loadedForRef.current && loadedForRef.current.session === session) return undefined;
			if (inFlightRef.current === session) return undefined;
		}
		inFlightRef.current = session;
		setFetching(true);
		setError(null);
		try {
			const list = await getMyTags();
			if (getSessionKey() !== session) return undefined; // another account's answer
			setTags(Object.freeze(reorderByOwner(list)));
			loadedForRef.current = { session };
			setLoaded(true);
			setVersion(v => v + 1);
			return list;
		} catch (err) {
			if (getSessionKey() !== session) return undefined;
			setError(err);
			return undefined;
		} finally {
			if (inFlightRef.current === session) {
				inFlightRef.current = undefined;
				setFetching(false);
			}
			setSettled(true);
		}
	}, []);

	useOnSessionChange(() => {
		loadedForRef.current = null;
		inFlightRef.current = undefined;
		setTags(EMPTY);
		setLoaded(false);
		setError(null);
		setFetching(false);
		if (wantedRef.current) load();
		else setSettled(false);
	});

	const refresh = useCallback(() => load({ force: true }), [load]);
	const replaceTags = useCallback(list => {
		setTags(Object.freeze(reorderByOwner(Array.isArray(list) ? list : [])));
	}, []);

	const tagsById = useMemo(() => indexTags(tags), [tags]);

	const value = useMemo(() => ({
		tags,
		tagsById,
		loading: fetching || !settled,
		loaded,
		error,
		version,
		ensureLoaded: load,
		refresh,
		replaceTags,
	}), [tags, tagsById, fetching, settled, loaded, error, version, load, refresh, replaceTags]);

	return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
}

/**
 * The context, or null outside a provider — for shared components that must
 * still render standalone (a chip row in a test harness). Every mount asks
 * `ensureLoaded`: a no-op while the list is fresh for this session.
 */
export function useOptionalTags() {
	const ctx = useContext(TagsContext);
	const ensureLoaded = ctx?.ensureLoaded;
	useEffect(() => {
		if (ensureLoaded) ensureLoaded();
	}, [ensureLoaded]);
	return ctx;
}

export function useTags() {
	const ctx = useOptionalTags();
	if (!ctx) throw new Error('useTags must be used within a TagsProvider');
	return ctx;
}

export default TagsContext;
